import React, { useState } from "react";
import { MailCheck, MailWarning, Send, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { FlaggedEmails } from "./EmailOpsPanels";

const Stat = ({ label, value, tone = "text-crimson", testId }) => (
  <div className="rounded-lg bg-parchment-card p-3 text-center">
    <p className={`font-mono-accent text-2xl font-bold ${tone}`} data-testid={testId}>{value}</p>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
  </div>
);

const DeliveryStats = () => {
  const { data: s } = useApi("/admin/email/stats?days=30");
  if (!s) return null;
  return (
    <div data-testid="email-stats">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        <Stat label="Sent" value={s.sent} testId="email-stat-sent" />
        <Stat label="Delivered" value={s.delivered} tone="text-palm" testId="email-stat-delivered" />
        <Stat label="Opened" value={s.opened} tone="text-palm" testId="email-stat-opened" />
        <Stat label="Clicked" value={s.clicked} tone="text-palm" testId="email-stat-clicked" />
        <Stat label="Bounced" value={s.bounced + s.complained} tone="text-destructive" testId="email-stat-bounced" />
        <Stat label="Failed" value={s.failed} tone="text-destructive" testId="email-stat-failed" />
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">Last {s.days} days. {s.webhook_configured ? "Delivered/opened/bounced come from Resend webhooks." : "Add RESEND_WEBHOOK_SECRET and point a Resend webhook at /api/webhooks/resend to see delivered, opened and bounced counts."}</p>
      {s.recent_bounces.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs" data-testid="email-bounces">
          {s.recent_bounces.map((b) => <li key={`${b.to}-${b.at}`} className="font-mono-accent text-destructive">{b.to} <span className="text-muted-foreground">· {b.type.replace("email.", "")} · {b.subject}</span></li>)}
        </ul>
      )}
    </div>
  );
};

const RetryFailed = ({ onDone }) => {
  const { data, reload } = useApi("/admin/email/failures");
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const canRetry = (user?.permissions || []).includes("broadcast");
  const n = data?.retryable || 0;
  const retry = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/admin/email/retry", { ids: [] }); toast.success(`Re-sending ${r.queued} email(s) in the background`); setTimeout(() => { reload(); onDone?.(); }, 4000); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!data || n === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-crimson/30 bg-crimson/5 p-3 text-sm" data-testid="email-retry-box">
      <span className="flex items-center gap-2 font-semibold text-crimson"><MailWarning size={16} /> {n} failed email(s) can be re-sent (rate-limit / provider errors)</span>
      {canRetry && <Button size="sm" onClick={retry} disabled={busy} className="gap-1 bg-crimson" data-testid="email-retry-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Retry all</Button>}
    </div>
  );
};

export const EmailHealth = () => {
  const { data, reload } = useApi("/push/email-health");
  const [busy, setBusy] = useState(false);
  const sendTest = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/push/email-health/test"); toast.success(`Test email sent to ${r.to}`); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); reload(); }
  };
  const failures = data?.recent_failures || [];
  const undeliverable = failures.filter((f) => f.code === "undeliverable_recipient");
  return (
    <div className="glass rounded-2xl p-6 space-y-3" data-testid="email-health">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-palm"><MailCheck size={18} /> Email delivery</h3>
        <Button size="sm" onClick={sendTest} disabled={busy || !data?.configured} className="bg-crimson" data-testid="email-test-btn"><Send size={14} className="mr-1" /> Send me a test</Button>
      </div>
      {data && (
        <p className="text-sm text-muted-foreground" data-testid="email-config">
          {data.configured ? `Connected via ${data.provider === "resend" ? "Resend" : "built-in"}` : "Not configured"} · From “{data.from_name}”{data.from_email ? ` <${data.from_email}>` : ""}{data.reply_to ? ` · Replies to ${data.reply_to}` : ""}
        </p>
      )}
      <DeliveryStats />
      <FlaggedEmails />
      <RetryFailed onDone={reload} />
      {undeliverable.length > 0 && (
        <div className="rounded-lg border border-gold/40 bg-gold-light p-3 text-sm" data-testid="email-undeliverable">
          <p className="flex items-center gap-2 font-semibold text-crimson"><MailWarning size={16} /> {undeliverable.length} message(s) blocked — recipient address looks undeliverable</p>
          <ul className="mt-2 space-y-1 text-xs text-foreground/80">
            {[...new Set(undeliverable.map((f) => f.to))].slice(0, 8).map((to) => <li key={to} className="font-mono-accent">{to}</li>)}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">Update these members’ email addresses in Members &amp; Vetting so reminders and welcome links reach them.</p>
        </div>
      )}
      {data && failures.length === 0 && <p className="text-xs text-palm" data-testid="email-no-failures">No delivery failures recorded.</p>}
    </div>
  );
};
